import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { IntlShape, useIntl } from 'react-intl';
import * as z from 'zod';

import {
  DesignObligation,
  ManualBuildingBlockInclusion,
  ProductBuildingBlock,
  ReevaluationRequest,
  TailorBuildingBlockInput,
} from '@/gql/graphql';

import {
  BuildingBlockDetails,
  BuildingBlockDetailsRow,
} from '@/components/elements/building-blocks/details-building-block/details-building-block.component';
import { T } from '@/components/i18n/message.i18n.component';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion/accordion.component';
import Button from '@/components/ui/button/button.component';
import { Checkbox } from '@/components/ui/checkbox/checkbox.component';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form/form.component';
import { Pill } from '@/components/ui/pill/pill.component';
import { Textarea } from '@/components/ui/textarea/textarea.component';

import { pretailoringPageMessages } from '@/pages/products/pre-tailoring/pre-tailoring.messages';

import { GroupedBuildingBlock } from '@/hooks/use-building-blocks/use-building-blocks.hook.core';
import { useToast } from '@/hooks/use-toast.hook';

import { useDialogStore } from '@/store/dialog.store';

import { useMutation } from '@apollo/client';
import { formValidationMessages, toastTranslations } from '../../all/products.messages';
import { tailorBuildingBlocks } from '../../products.requests';
import { tailorFormsTranslations } from '../tailoring-forms.messages';
import { tailoringMessages } from '../tailoring.messages';

function formSchema(
  t: IntlShape,
  bbs: ProductBuildingBlock[],
): z.ZodObject<{
  inclusionExplanation: z.ZodPipeline<z.ZodEffects<z.ZodString, string, string>, z.ZodString>;
  buildingBlocks: z.ZodObject<{ [key: string]: z.ZodBoolean }>;
}> {
  const bbValidation: { [key: string]: any } = {};

  bbs.map((bb) => {
    bbValidation[bb.id] = z.boolean();
  });

  return z.object({
    inclusionExplanation: z
      .string()
      .transform((value) => value.trim())
      .pipe(
        z
          .string()
          .min(1, {
            message: t.formatMessage(formValidationMessages.minimumLength, {
              minLength: 1,
            }),
          })
          .max(60, {
            message: t.formatMessage(formValidationMessages.maximumLength, {
              maxLength: 60,
            }),
          }),
      ),
    buildingBlocks: z.object({
      ...bbValidation,
    }),
  });
}

const generateDefaultValues = (bbs: ProductBuildingBlock[]) => {
  const bbValues: { [key: string]: boolean } = {};

  bbs.map((bb) => {
    bbValues[bb.id] = true;
  });

  return {
    inclusionExplanation: '',
    buildingBlocks: {
      ...bbValues,
    },
  };
};

const isAnyBBSelected = (values: z.infer<ReturnType<typeof formSchema>>) => {
  return Object.keys(values.buildingBlocks).some((key) => values.buildingBlocks[key] === true);
};

type TailorDesignObligationFormProps = {
  includedBB: boolean;
  groupedBB: GroupedBuildingBlock<DesignObligation>;
  afterAdd?: (bbs: TailorBuildingBlockInput[]) => void;
};

export const TailorDesignObligationForm = ({ includedBB, groupedBB, afterAdd }: TailorDesignObligationFormProps) => {
  const intl = useIntl();
  const { toast } = useToast();

  const [tailorBuildingBlocksMutation, { loading }] = useMutation(tailorBuildingBlocks);

  const { handleDialogClose } = useDialogStore((state) => ({
    handleDialogClose: state.closeDialog,
  }));

  const form = useForm<z.infer<ReturnType<typeof formSchema>>>({
    resolver: zodResolver(formSchema(intl, groupedBB.buildingBlocks)),
    defaultValues: generateDefaultValues(groupedBB.buildingBlocks),
  });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async function onSubmit(values: z.infer<ReturnType<typeof formSchema>>) {
    const buildingBlocks = groupedBB.buildingBlocks
      .filter((bb) => values.buildingBlocks[bb.id])
      .map((bb) => ({
        buildingBlockId: bb.id,
        productId: bb.productId,
        included: includedBB ? ManualBuildingBlockInclusion.Included : ManualBuildingBlockInclusion.Excluded,
        inclusionExplanation: values.inclusionExplanation,
        reevaluationRequest: ReevaluationRequest.Requested,
      }));

    await tailorBuildingBlocksMutation({
      variables: {
        tailorBuildingBlocksInput: buildingBlocks,
      },
      refetchQueries: ['getProduct'],
    });

    handleDialogClose();

    toast({
      variant: 'default',
      description: intl.formatMessage(
        !includedBB ? toastTranslations.excludeBuildingBlocksToast : toastTranslations.includeBuildingBlocksToast,
      ),
    });
    if (afterAdd) await afterAdd(buildingBlocks);
  }

  return (
    <div className="overflow-x-auto flex-1 flex flex-col justify-between items-start gap-y-3 w-full">
      <div className="w-full">
        <Pill data-testid="tailor-do-form-do-pill" className="mb-3" variant="do" text={groupedBB.name} />
        <BuildingBlockDetailsRow
          className="mb-6"
          label={<T {...pretailoringPageMessages.singleBuildingBlockDataBlockDescription} />}
          value={groupedBB.description}
          context="tailor-do-form-do-description"
        />
        <Form {...form} data-testid="tailor-do-form-form-container">
          <form
            id="tailor-do-form"
            data-testid="tailor-do-form"
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4 active:border-none"
          >
            <FormField
              control={form.control}
              name="inclusionExplanation"
              // eslint-disable-next-line react/jsx-handler-names
              render={({ field }) => {
                return (
                  <FormItem>
                    <div className="flex flex-row justify-between items-center mb-3">
                      <FormLabel data-testid="tailor-do-form-label-explanation" required>
                        {includedBB && <T {...tailorFormsTranslations.bbTailoringFormInclusionExplanationLabel} />}
                        {!includedBB && <T {...tailorFormsTranslations.bbTailoringFormExclusionExplanationLabel} />}
                      </FormLabel>
                      <FormMessage data-testid="tailor-do-form-error-explanation" />
                    </div>
                    <FormControl>
                      <Textarea
                        data-testid="tailor-do-form-textarea-explanation"
                        placeholder={intl.formatMessage(
                          !includedBB
                            ? tailorFormsTranslations.doTailoringFormExclusionExplanationPlaceholder
                            : tailorFormsTranslations.doTailoringFormInclusionExplanationPlaceholder,
                        )}
                        {...field}
                      />
                    </FormControl>
                  </FormItem>
                );
              }}
            />
            <p data-testid="tailor-do-form-building-blocks-label" className="typo-secondary text-secondary-text mb-3">
              {includedBB && <T {...tailorFormsTranslations.doTailoringFormBBSubtitleIncluded} />}
              {!includedBB && <T {...tailorFormsTranslations.doTailoringFormBBSubtitleExcluded} />}
            </p>
            <Accordion type="single" collapsible className="w-full flex flex-col gap-y-4">
              {groupedBB.buildingBlocks.map((bb) => {
                return (
                  <AccordionItem key={bb.id} value={bb.id || ''} className="border rounded border-other-stroke p-4">
                    <div className="flex flex-row justify-between items-center gap-x-6">
                      <div className="flex flex-row justify-between items-center flex-1">
                        <Pill variant="bb" text={bb.name || ''} />
                        <FormField
                          control={form.control}
                          name={`buildingBlocks.${bb.id}`}
                          // eslint-disable-next-line react/jsx-handler-names
                          render={({ field }) => (
                            <FormItem>
                              <FormMessage />
                              <div className="flex flex-row justify-start gap-x-2 items-center">
                                <FormControl>
                                  <Checkbox
                                    data-testid={`tailor-do-form-bbs-${bb.id}-checkbox`}
                                    checked={field.value as boolean}
                                    // eslint-disable-next-line react/jsx-handler-names
                                    onCheckedChange={field.onChange}
                                  />
                                </FormControl>
                                <FormLabel
                                  data-testid={`tailor-do-form-bbs-${bb.id}-checkbox-label`}
                                  className="cursor-pointer"
                                >
                                  {includedBB && <T {...tailoringMessages.include} />}
                                  {!includedBB && <T {...tailoringMessages.exclude} />}
                                </FormLabel>
                              </div>
                            </FormItem>
                          )}
                        />
                      </div>

                      <AccordionTrigger
                        className="w-full typo-primary-500"
                        data-testid={`tailor-do-form-bbs-${bb.id}-accordion-trigger`}
                      ></AccordionTrigger>
                    </div>

                    <AccordionContent
                      className="gap-y-4 flex flex-col"
                      data-testid={`tailor-do-form-bbs-${bb.id}-accordion-content`}
                    >
                      <BuildingBlockDetails buildingBlock={bb} />
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </form>
        </Form>
      </div>
      <div className="flex flex-row justify-between w-full">
        <Button
          data-testid="tailor-do-form-cancel-button"
          onClick={handleDialogClose}
          type="button"
          variant="secondary"
        >
          <T {...tailorFormsTranslations.bbTailoringFormCancelButton} />{' '}
        </Button>
        <Button
          loading={loading}
          data-testid="tailor-do-form-submit-button"
          form="tailor-do-form"
          disabled={!form.formState.isDirty || !isAnyBBSelected(form.getValues())}
          type="submit"
          variant="default"
        >
          {!includedBB && <T {...tailorFormsTranslations.doTailoringFormExclusionSubmitButton} />}
          {includedBB && <T {...tailorFormsTranslations.doTailoringFormInclusionSubmitButton} />}
        </Button>
      </div>
    </div>
  );
};
